import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import usePermissions from "src/hooks/usePermissions";
import { getAuthUser } from "src/helpers/auth";
import { acceptTicket, deleteTicket, getTicketCategories, getTickets, postTicket, postTicketBulkAction, getUsersAll, downloadTicketCsv } from "src/helpers/backend_helper";
import { normalizeUserOptions } from "src/helpers/normalizeUserOptions";
import { toast } from "react-toastify";
import { ticketBus } from "src/helpers/ticketEventBus";
import Select from "../../components/Common/Select";

type TicketStatus = "open" | "in_progress" | "resolved" | "closed";

type TicketUser = {
  id: number;
  name: string;
};

type TicketCategoryItem = {
  id: number;
  name: string;
  slug: string;
  color: string;
};

type TicketPermissions = {
  can_update?: boolean;
  can_delete?: boolean;
  can_accept?: boolean;
  can_reply?: boolean;
};

type TicketItem = {
  id: number;
  uuid?: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: number;
  creator?: TicketUser | null;
  assignee?: TicketUser | null;
  last_message_at?: string | null;
  created_at?: string | null;
  permissions?: TicketPermissions;
  first_response_at?: string | null;
  first_response_due_at?: string | null;
  resolution_due_at?: string | null;
  sla_first_response_breached?: boolean;
  sla_resolution_breached?: boolean;
  escalated_at?: string | null;
  unread_count?: number;
  source?: string;
  category_id?: number | null;
  category?: TicketCategoryItem | null;
};

type Pagination = {
  total: number;
  current_page: number;
  per_page: number;
  total_page: number;
  next_page?: number | null;
  prev_page?: number | null;
};

type FilterOption = {
  value: string;
  label: string;
};

type TicketListFilters = {
  status?: string[];
  priority?: string[];
  search?: string;
  category_id?: string[];
};

const STATUS_OPTIONS: FilterOption[] = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const PRIORITY_FILTER_OPTIONS: FilterOption[] = [
  { value: "1", label: "Critical" },
  { value: "2", label: "High" },
  { value: "3", label: "Medium" },
  { value: "4", label: "Low" },
];

const statusBadgeColor = (status: TicketStatus): string => {
  switch (status) {
    case "open":
      return "warning";
    case "in_progress":
      return "info";
    case "resolved":
      return "success";
    case "closed":
      return "secondary";
    default:
      return "light";
  }
};

const statusLabel = (status: TicketStatus): string => {
  switch (status) {
    case "open":
      return "Open";
    case "in_progress":
      return "In progress";
    case "resolved":
      return "Resolved";
    case "closed":
      return "Closed";
    default:
      return status;
  }
};

const priorityBadgeColor = (priority: number): string => {
  switch (priority) {
    case 1:
      return "danger";
    case 2:
      return "warning";
    case 3:
      return "info";
    case 4:
      return "secondary";
    default:
      return "light";
  }
};

const priorityLabel = (priority: number): string => {
  switch (priority) {
    case 1:
      return "Critical";
    case 2:
      return "High";
    case 3:
      return "Medium";
    case 4:
      return "Low";
    default:
      return "Unknown";
  }
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const getApiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message
  || error?.message
  || fallback;

const getSelectedOptions = (options: FilterOption[], selectedValues: string[]): FilterOption[] =>
  options.filter((option) => selectedValues.includes(option.value));

const mapSelectValues = (options: readonly FilterOption[] | null | undefined): string[] =>
  (options || []).map((option: FilterOption) => option.value);

const serializeMultiFilter = (values: string[]): string | undefined =>
  values.length > 0 ? values.join(",") : undefined;

const isTelegramBotTicket = (ticket: TicketItem): boolean => ticket.source === "telegram";

const TicketSourceBadge = ({ ticket }: { ticket: TicketItem }) => (
  isTelegramBotTicket(ticket) ? (
    <Badge color="info" className="font-size-10">
      Telegram Bot
    </Badge>
  ) : null
);

type TicketRowProps = {
  ticket: TicketItem;
  deletingTicketId: number | null;
  onAccept: (ticket: TicketItem) => void;
  onDelete: (ticket: TicketItem) => void;
  selected?: boolean;
  onToggleSelect?: (id: number) => void;
  showCheckbox?: boolean;
  disableCheckbox?: boolean;
};

const TicketRow = React.memo(({ ticket, deletingTicketId, onAccept, onDelete, selected, onToggleSelect, showCheckbox, disableCheckbox }: TicketRowProps) => {
  const canAcceptRow = ticket.permissions?.can_accept === true && !ticket.assignee;
  const canDeleteRow = ticket.permissions?.can_delete === true;
  const unreadCount = Number(ticket.unread_count || 0);

  return (
    <tr className={selected ? "table-active" : ""}>
      {showCheckbox && (
        <td style={{ width: 36 }}>
          <Input
            type="checkbox"
            checked={!!selected}
            disabled={disableCheckbox}
            onChange={() => onToggleSelect?.(ticket.id)}
          />
        </td>
      )}
      <td>#{ticket.id}</td>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div className="fw-semibold">{ticket.subject}</div>
          <TicketSourceBadge ticket={ticket} />
          {unreadCount > 0 && (
            <Badge color="danger" className="font-size-10">
              {unreadCount} new
            </Badge>
          )}
        </div>
        <small className="text-muted">
          {ticket.description.length > 120 ? `${ticket.description.slice(0, 120)}...` : ticket.description}
        </small>
      </td>
      <td>
        <Badge color={statusBadgeColor(ticket.status)}>{statusLabel(ticket.status)}</Badge>
      </td>
      <td>
        <Badge color={priorityBadgeColor(ticket.priority)}>
          {priorityLabel(ticket.priority)}
        </Badge>
      </td>
      <td>
        {ticket.category ? (
          <Badge style={{ backgroundColor: ticket.category.color, color: "#fff" }} className="font-size-10">
            {ticket.category.name}
          </Badge>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td>{ticket.creator?.name || "—"}</td>
      <td>{ticket.assignee?.name || "—"}</td>
      <td>
        {ticket.status === "closed" || ticket.status === "resolved" ? (
          <span className="text-muted">—</span>
        ) : ticket.sla_first_response_breached || ticket.sla_resolution_breached ? (
          <Badge color="danger" className="font-size-10">Breached</Badge>
        ) : ticket.escalated_at ? (
          <Badge color="danger" className="font-size-10">Escalated</Badge>
        ) : (
          <Badge color="success" className="font-size-10">OK</Badge>
        )}
      </td>
      <td>{formatDateTime(ticket.last_message_at || ticket.created_at)}</td>
      <td className="text-end">
        <div className="d-flex gap-2 justify-content-end">
          <Link to={`/tickets/${ticket.uuid || ticket.id}`} className="btn btn-sm btn-primary">
            Open
          </Link>
          {canAcceptRow && (
            <Button size="sm" color="info" onClick={() => onAccept(ticket)}>
              Accept
            </Button>
          )}
          {canDeleteRow && (
            <Button
              size="sm"
              color="danger"
              onClick={() => onDelete(ticket)}
              disabled={deletingTicketId === ticket.id}
            >
              Delete
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
});

TicketRow.displayName = "TicketRow";

type ViewMode = "table" | "cards";
const VIEW_MODE_KEY = "tickets_view_mode";
const FILTERS_OPEN_KEY = "tickets_filters_open";

const getStoredViewMode = (): ViewMode => {
  try {
    const stored = localStorage.getItem(VIEW_MODE_KEY);
    if (stored === "table" || stored === "cards") return stored;
  } catch {}
  return "table";
};

const getStoredFiltersOpen = (): boolean => {
  try {
    return localStorage.getItem(FILTERS_OPEN_KEY) !== "false";
  } catch {}
  return true;
};

type TicketsQueryState = {
  page: number;
  viewMode: ViewMode;
  filtersOpen: boolean;
  status: string[];
  priority: string[];
  search: string;
  category: string[];
};

const parseQueryList = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsed = Number(value);

  if (!Number.isInteger(parsed) || parsed < 1) {
    return fallback;
  }

  return parsed;
};

const parseTicketsQueryState = (searchParams: URLSearchParams): TicketsQueryState => {
  const view = searchParams.get("view");
  const filters = searchParams.get("filters");

  return {
    page: parsePositiveInteger(searchParams.get("page"), 1),
    viewMode: view === "cards" || view === "table" ? view : getStoredViewMode(),
    filtersOpen: filters === null ? getStoredFiltersOpen() : filters !== "0",
    status: parseQueryList(searchParams.get("status")),
    priority: parseQueryList(searchParams.get("priority")),
    search: searchParams.get("q")?.trim() || "",
    category: parseQueryList(searchParams.get("category_id")),
  };
};

const buildTicketsSearchParams = (state: TicketsQueryState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.page > 1) {
    params.set("page", String(state.page));
  }

  if (state.viewMode !== "table") {
    params.set("view", state.viewMode);
  }

  if (!state.filtersOpen) {
    params.set("filters", "0");
  }

  const status = serializeMultiFilter(state.status);
  const priority = serializeMultiFilter(state.priority);
  const category = serializeMultiFilter(state.category);
  const search = state.search.trim();

  if (status) {
    params.set("status", status);
  }

  if (priority) {
    params.set("priority", priority);
  }

  if (category) {
    params.set("category_id", category);
  }

  if (search) {
    params.set("q", search);
  }

  return params;
};

const slaInfo = (ticket: TicketItem): { label: string; color: string } => {
  if (ticket.status === "closed" || ticket.status === "resolved") {
    return { label: "—", color: "secondary" };
  }
  if (ticket.sla_first_response_breached || ticket.sla_resolution_breached) {
    return { label: "Breached", color: "danger" };
  }
  if (ticket.escalated_at) {
    return { label: "Escalated", color: "danger" };
  }
  return { label: "OK", color: "success" };
};

type TicketCardProps = {
  ticket: TicketItem;
  deletingTicketId: number | null;
  onAccept: (ticket: TicketItem) => void;
  onDelete: (ticket: TicketItem) => void;
  selected?: boolean;
  onToggleSelect?: (id: number) => void;
  showCheckbox?: boolean;
  disableCheckbox?: boolean;
};

const TicketCard = React.memo(({ ticket, deletingTicketId, onAccept, onDelete, selected, onToggleSelect, showCheckbox, disableCheckbox }: TicketCardProps) => {
  const canAcceptRow = ticket.permissions?.can_accept === true && !ticket.assignee;
  const canDeleteRow = ticket.permissions?.can_delete === true;
  const unreadCount = Number(ticket.unread_count || 0);
  const sla = slaInfo(ticket);

  const priorityProgress: Record<number, { percent: number; color: string }> = {
    1: { percent: 100, color: "danger" },
    2: { percent: 75, color: "warning" },
    3: { percent: 50, color: "info" },
    4: { percent: 25, color: "secondary" },
  };
  const pp = priorityProgress[ticket.priority] || { percent: 0, color: "light" };

  return (
    <Col xl={4} md={6} sm={12}>
      <Card className={`mb-3 border ${selected ? "border-primary shadow-sm" : ""}`} style={{ transition: "box-shadow .15s, border-color .15s" }}>
        <CardBody className="p-3">
          {/* Header: ID + Status + Checkbox */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              {showCheckbox && (
                <Input
                  type="checkbox"
                  checked={!!selected}
                  disabled={disableCheckbox}
                  onChange={() => onToggleSelect?.(ticket.id)}
                  className="me-1"
                />
              )}
              <span className="text-muted fw-medium font-size-13">#{ticket.id}</span>
            </div>
            <Badge color={statusBadgeColor(ticket.status)} className="font-size-12">
              {statusLabel(ticket.status)}
            </Badge>
          </div>

          {/* Subject */}
          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
            <h5 className="font-size-15 mb-0">
              <Link to={`/tickets/${ticket.uuid || ticket.id}`} className="text-dark">
                {ticket.subject}
              </Link>
            </h5>
            <TicketSourceBadge ticket={ticket} />
            {unreadCount > 0 && (
              <Badge color="danger" pill className="font-size-10">{unreadCount} new</Badge>
            )}
          </div>

          {/* Description preview */}
          <p className="text-muted mb-3" style={{ fontSize: "13px", lineHeight: "1.5", minHeight: "40px" }}>
            {ticket.description.length > 140 ? `${ticket.description.slice(0, 140)}...` : ticket.description}
          </p>

          {/* Priority bar */}
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Priority</small>
              <Badge color={priorityBadgeColor(ticket.priority)} className="font-size-10">
                {priorityLabel(ticket.priority)}
              </Badge>
            </div>
            <Progress value={pp.percent} color={pp.color} style={{ height: "4px" }} />
          </div>

          {/* Info grid */}
          <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: "13px" }}>
            <div className="flex-fill" style={{ minWidth: "45%" }}>
              <small className="text-muted d-block">Category</small>
              {ticket.category ? (
                <Badge style={{ backgroundColor: ticket.category.color, color: "#fff" }} className="font-size-10 mt-1">
                  {ticket.category.name}
                </Badge>
              ) : (
                <span className="text-muted">—</span>
              )}
            </div>
            <div className="flex-fill" style={{ minWidth: "45%" }}>
              <small className="text-muted d-block">SLA</small>
              <Badge color={sla.color} className="font-size-10 mt-1">{sla.label}</Badge>
            </div>
            <div className="flex-fill" style={{ minWidth: "45%" }}>
              <small className="text-muted d-block">Creator</small>
              <span className="fw-medium">{ticket.creator?.name || "—"}</span>
            </div>
            <div className="flex-fill" style={{ minWidth: "45%" }}>
              <small className="text-muted d-block">Assignee</small>
              <span className="fw-medium">{ticket.assignee?.name || "—"}</span>
            </div>
          </div>

          {/* Footer: date + actions */}
          <div className="d-flex align-items-center justify-content-between pt-2 border-top">
            <small className="text-muted">
              {formatDateTime(ticket.last_message_at || ticket.created_at)}
            </small>
            <div className="d-flex gap-2">
              <Link to={`/tickets/${ticket.uuid || ticket.id}`} className="btn btn-sm btn-primary">
                Open
              </Link>
              {canAcceptRow && (
                <Button size="sm" color="info" onClick={() => onAccept(ticket)}>
                  Accept
                </Button>
              )}
              {canDeleteRow && (
                <Button
                  size="sm"
                  color="danger"
                  onClick={() => onDelete(ticket)}
                  disabled={deletingTicketId === ticket.id}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
});

TicketCard.displayName = "TicketCard";

const TicketsPage = () => {
  document.title = "Ticket System | Dashboard";

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const initialQueryStateRef = useRef<TicketsQueryState>(parseTicketsQueryState(searchParams));
  const initialQueryState = initialQueryStateRef.current;

  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string>("");
  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    current_page: initialQueryState.page,
    per_page: 20,
    total_page: 1,
    next_page: null,
    prev_page: null,
  });
  const [statusFilter, setStatusFilter] = useState<string[]>(initialQueryState.status);
  const [priorityFilter, setPriorityFilter] = useState<string[]>(initialQueryState.priority);
  const [searchFilter, setSearchFilter] = useState<string>(initialQueryState.search);
  const [categoryFilter, setCategoryFilter] = useState<string[]>(initialQueryState.category);
  const [categories, setCategories] = useState<TicketCategoryItem[]>([]);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [priority, setPriority] = useState<number>(2);
  const [createCategoryId, setCreateCategoryId] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleteModalTicket, setDeleteModalTicket] = useState<TicketItem | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<string>("");
  const [deletingTicketId, setDeletingTicketId] = useState<number | null>(null);
  const initialLoadStartedRef = useRef<boolean>(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSearchInitializedRef = useRef<boolean>(false);
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<string>("");
  const [bulkValue, setBulkValue] = useState<string>("");
  const [bulkProcessing, setBulkProcessing] = useState<boolean>(false);
  const [bulkAssignUsers, setBulkAssignUsers] = useState<Array<{ id: number; name: string }>>([]);
  const [exporting, setExporting] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>(initialQueryState.viewMode);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(initialQueryState.filtersOpen);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch {}
  }, []);

  const toggleFilters = useCallback(() => {
    setFiltersOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(FILTERS_OPEN_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  const canView = hasPermission("view-tickets");
  const canCreate = hasPermission("create-tickets");
  const canBulk = hasPermission("bulk-tickets");
  const currentUserId = useMemo(() => Number(getAuthUser()?.user_id || 0), []);
  const layoutMode = useSelector((state: any) => localStorage.getItem("layoutMode") || state.Layout.layoutMode);
  const categoryFilterOptions = useMemo<FilterOption[]>(
    () => categories.map((category) => ({
      value: String(category.id),
      label: category.name,
    })),
    [categories]
  );
  const filterSelectStyles = useMemo(() => {
    if (layoutMode !== "dark") {
      return undefined;
    }

    return {
      control: (base: any, state: any) => ({
        ...base,
        backgroundColor: "#2a3042",
        borderColor: state.isFocused ? "#556ee6" : "#32394e",
        boxShadow: state.isFocused ? "0 0 0 1px #556ee6" : "none",
        minHeight: 38,
        "&:hover": {
          borderColor: "#556ee6",
        },
      }),
      menu: (base: any) => ({
        ...base,
        backgroundColor: "#2a3042",
        border: "1px solid #32394e",
      }),
      menuList: (base: any) => ({
        ...base,
        backgroundColor: "#2a3042",
      }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "#556ee6"
          : state.isFocused
            ? "#32394e"
            : "#2a3042",
        color: "#eff2f7",
        "&:active": {
          backgroundColor: "#556ee6",
        },
      }),
      multiValue: (base: any) => ({
        ...base,
        backgroundColor: "#32394e",
      }),
      multiValueLabel: (base: any) => ({
        ...base,
        color: "#eff2f7",
      }),
      multiValueRemove: (base: any) => ({
        ...base,
        color: "#adb5bd",
        "&:hover": {
          backgroundColor: "#495057",
          color: "#fff",
        },
      }),
      input: (base: any) => ({
        ...base,
        color: "#eff2f7",
      }),
      placeholder: (base: any) => ({
        ...base,
        color: "#adb5bd",
      }),
      singleValue: (base: any) => ({
        ...base,
        color: "#eff2f7",
      }),
      dropdownIndicator: (base: any) => ({
        ...base,
        color: "#adb5bd",
        "&:hover": {
          color: "#eff2f7",
        },
      }),
      clearIndicator: (base: any) => ({
        ...base,
        color: "#adb5bd",
        "&:hover": {
          color: "#eff2f7",
        },
      }),
      indicatorSeparator: (base: any) => ({
        ...base,
        backgroundColor: "#495057",
      }),
    };
  }, [layoutMode]);

  const loadTickets = useCallback(
    async (page: number = 1, filters?: TicketListFilters, silent: boolean = false) => {
      if (!silent) {
        setLoadError("");
        setLoading(true);
      }
      try {
        const effectiveStatus = Array.isArray(filters?.status) ? filters.status : statusFilter;
        const effectivePriority = Array.isArray(filters?.priority) ? filters.priority : priorityFilter;
        const effectiveSearch = typeof filters?.search === "string" ? filters.search : searchFilter;
        const effectiveCategory = Array.isArray(filters?.category_id) ? filters.category_id : categoryFilter;

        const response: any = await getTickets("", {
          page,
          per_page: pagination.per_page,
          status: serializeMultiFilter(effectiveStatus),
          priority: serializeMultiFilter(effectivePriority),
          q: effectiveSearch || undefined,
          category_id: serializeMultiFilter(effectiveCategory),
        });

        const rawItems = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        setLoadError("");
        setTickets(rawItems as TicketItem[]);
        setPagination((prev) => ({
          ...prev,
          ...(response?.pagination || {}),
          current_page: response?.pagination?.current_page || page,
        }));
      } catch (error: any) {
        if (!silent) {
          setLoadError(getApiErrorMessage(error, "Failed to load tickets."));
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [categoryFilter, pagination.per_page, priorityFilter, searchFilter, statusFilter]
  );

  const matchesCurrentFilters = useCallback(
    (ticket: TicketItem): boolean => {
      if (statusFilter.length > 0 && !statusFilter.includes(ticket.status)) {
        return false;
      }

      if (priorityFilter.length > 0 && !priorityFilter.includes(String(ticket.priority))) {
        return false;
      }

      const ticketCategoryId = ticket.category_id != null ? String(ticket.category_id) : "";
      if (categoryFilter.length > 0 && !categoryFilter.includes(ticketCategoryId)) {
        return false;
      }

      const q = searchFilter.trim().toLowerCase();
      if (!q) {
        return true;
      }

      return (
        ticket.subject.toLowerCase().includes(q)
        || ticket.description.toLowerCase().includes(q)
      );
    },
    [categoryFilter, priorityFilter, searchFilter, statusFilter]
  );

  const upsertTicketRow = useCallback(
    (incoming: TicketItem) => {
      if (!matchesCurrentFilters(incoming)) {
        setTickets((prev) => prev.filter((item) => item.id !== incoming.id));
        return;
      }

      setTickets((prev) => {
        const existingIndex = prev.findIndex((item) => item.id === incoming.id);
        if (existingIndex >= 0) {
          const next = [...prev];
          next[existingIndex] = incoming;
          return next;
        }

        if (pagination.current_page !== 1) {
          return prev;
        }

        return [incoming, ...prev].slice(0, pagination.per_page);
      });
    },
    [matchesCurrentFilters, pagination.current_page, pagination.per_page]
  );

  useEffect(() => {
    if (!canView) {
      navigate("/no-access");
      return;
    }

    if (initialLoadStartedRef.current) {
      return;
    }

    initialLoadStartedRef.current = true;

    void loadTickets(initialQueryState.page, {
      status: initialQueryState.status,
      priority: initialQueryState.priority,
      search: initialQueryState.search,
      category_id: initialQueryState.category,
    });
    getTicketCategories()
      .then((res: any) => setCategories(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {});
  }, [canView, initialQueryState, loadTickets, navigate]);

  useEffect(() => {
    const nextParams = buildTicketsSearchParams({
      page: pagination.current_page,
      viewMode,
      filtersOpen,
      status: statusFilter,
      priority: priorityFilter,
      search: searchFilter,
      category: categoryFilter,
    });
    const nextQuery = nextParams.toString();
    const currentQuery = searchParams.toString();

    if (nextQuery !== currentQuery) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    categoryFilter,
    filtersOpen,
    pagination.current_page,
    priorityFilter,
    searchFilter,
    searchParams,
    setSearchParams,
    statusFilter,
    viewMode,
  ]);

  useEffect(() => {
    if (!autoSearchInitializedRef.current) {
      autoSearchInitializedRef.current = true;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setPagination((prev) => (
        prev.current_page === 1
          ? prev
          : { ...prev, current_page: 1 }
      ));
      void loadTickets(1, {
        status: statusFilter,
        priority: priorityFilter,
        search: searchFilter,
        category_id: categoryFilter,
      }, true);
    }, 400);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [categoryFilter, loadTickets, priorityFilter, searchFilter, statusFilter]);

  useEffect(() => {
    return ticketBus.on("ticket-created", (payload) => {
      if (!payload?.ticket_uuid) return;
      if (payload.ticket) {
        upsertTicketRow(payload.ticket as TicketItem);
      } else {
        void loadTickets(1, undefined, true);
      }
    });
  }, [loadTickets, upsertTicketRow]);

  useEffect(() => {
    return ticketBus.on("ticket-updated", (payload) => {
      if (!payload?.ticket_uuid) return;
      if (payload.ticket) {
        upsertTicketRow(payload.ticket as TicketItem);
      } else {
        void loadTickets(1, undefined, true);
      }
    });
  }, [loadTickets, upsertTicketRow]);

  useEffect(() => {
    return ticketBus.on("ticket-accepted", (payload) => {
      if (!payload?.ticket_uuid) return;
      if (payload.ticket) {
        upsertTicketRow(payload.ticket as TicketItem);
      } else {
        void loadTickets(1, undefined, true);
      }
    });
  }, [loadTickets, upsertTicketRow]);

  useEffect(() => {
    return ticketBus.on("ticket-deleted", (payload) => {
      if (!payload?.ticket_id) return;
      setTickets((prev) => prev.filter((t) => t.id !== payload.ticket_id));
    });
  }, []);

  useEffect(() => {
    return ticketBus.on("ws-reconnected", () => {
      void loadTickets(pagination.current_page, undefined, true);
    });
  }, [loadTickets, pagination.current_page]);

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!canCreate) return;
    setSubmitting(true);
    try {
      const response: any = await postTicket({
        subject,
        description,
        priority,
        category_id: createCategoryId ? Number(createCategoryId) : undefined,
      });
      const createdTicket: TicketItem | null = response?.data ?? null;

      toast.success("Ticket created");
      setSubject("");
      setDescription("");
      setPriority(2);
      setCreateCategoryId("");
      setShowCreateForm(false);
      if (createdTicket) {
        upsertTicketRow(createdTicket);
      } else {
        await loadTickets(1, undefined, true);
      }
      ticketBus.emit("ticket-count-refresh");
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to create ticket."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = useCallback(async (ticket: TicketItem) => {
    const routeKey = ticket.uuid || ticket.id;
    try {
      const response: any = await acceptTicket(routeKey);
      const updatedTicket: TicketItem | null = response?.data ?? null;
      if (updatedTicket) {
        upsertTicketRow(updatedTicket);
      } else {
        await loadTickets(pagination.current_page);
      }
      ticketBus.emit("ticket-count-refresh");
      toast.success("Ticket accepted");
    } catch {
      await loadTickets(pagination.current_page);
      toast.error("Failed to accept ticket");
    }
  }, [loadTickets, pagination.current_page, upsertTicketRow]);

  const isOwnTicket = useCallback((ticket: TicketItem) => {
    return currentUserId > 0 && Number(ticket.creator?.id || 0) === currentUserId;
  }, [currentUserId]);

  const ownTickets = useMemo(() => tickets.filter(isOwnTicket), [tickets, isOwnTicket]);

  const toggleSelectTicket = useCallback((id: number) => {
    const ticket = tickets.find((t) => t.id === id);
    if (ticket && !isOwnTicket(ticket)) return;
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [tickets, isOwnTicket]);

  const toggleSelectAll = useCallback(() => {
    setSelectedTicketIds((prev) => {
      if (prev.size === ownTickets.length && ownTickets.length > 0) {
        return new Set();
      }
      return new Set(ownTickets.map((t) => t.id));
    });
  }, [ownTickets]);

  const handleBulkAction = async () => {
    if (!bulkAction || selectedTicketIds.size === 0) return;
    setBulkProcessing(true);
    try {
      const data: { ticket_ids: number[]; action: string; value?: any } = {
        ticket_ids: Array.from(selectedTicketIds),
        action: bulkAction,
      };
      if (bulkAction === "assign" || bulkAction === "change_priority") {
        data.value = bulkValue;
      }
      if (bulkAction === "change_status") {
        data.value = bulkValue;
      }
      const resp: any = await postTicketBulkAction(data);
      toast.success(resp?.message || "Bulk action completed");
      setSelectedTicketIds(new Set());
      setBulkAction("");
      setBulkValue("");
      await loadTickets(pagination.current_page);
      ticketBus.emit("ticket-count-refresh");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Bulk action failed");
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params: Record<string, string> = {};
      const serializedStatus = serializeMultiFilter(statusFilter);
      const serializedPriority = serializeMultiFilter(priorityFilter);
      const serializedCategory = serializeMultiFilter(categoryFilter);
      const trimmedSearch = searchFilter.trim();
      if (serializedStatus) params.status = serializedStatus;
      if (serializedPriority) params.priority = serializedPriority;
      if (serializedCategory) params.category_id = serializedCategory;
      if (trimmedSearch) params.q = trimmedSearch;
      const blob: any = await downloadTicketCsv(params);
      const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tickets-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Failed to export tickets");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (bulkAction === "assign" && bulkAssignUsers.length === 0) {
      getUsersAll().then((resp: any) => {
        setBulkAssignUsers(normalizeUserOptions(resp));
      }).catch(() => {});
    }
  }, [bulkAction, bulkAssignUsers.length]);

  const openDeleteModal = useCallback((ticket: TicketItem) => {
    setDeleteModalTicket(ticket);
    setDeleteConfirmation("");
  }, []);

  const closeDeleteModal = () => {
    if (deletingTicketId !== null) {
      return;
    }

    setDeleteModalTicket(null);
    setDeleteConfirmation("");
  };

  const handleDelete = async () => {
    if (!deleteModalTicket) return;

    const expectedConfirmation = `delete ${deleteModalTicket.id}`;
    if (deleteConfirmation.trim() !== expectedConfirmation) {
      toast.error(`Confirmation phrase must be exactly "${expectedConfirmation}".`);
      return;
    }

    const routeKey = deleteModalTicket.uuid || deleteModalTicket.id;
    setDeletingTicketId(deleteModalTicket.id);
    try {
      await deleteTicket(routeKey, expectedConfirmation);
      toast.success("Ticket deleted");
      const deletedId = deleteModalTicket.id;
      setDeleteModalTicket(null);
      setDeleteConfirmation("");
      setTickets((prev) => prev.filter((t) => t.id !== deletedId));
      ticketBus.emit("ticket-count-refresh");
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to delete ticket."));
    } finally {
      setDeletingTicketId(null);
    }
  };

  const hasActiveFilters = useMemo(() => {
    return searchFilter.trim().length > 0
      || statusFilter.length > 0
      || priorityFilter.length > 0
      || categoryFilter.length > 0;
  }, [categoryFilter, priorityFilter, searchFilter, statusFilter]);
  const deleteExpectedPhrase = deleteModalTicket ? `delete ${deleteModalTicket.id}` : "";
  const canConfirmDelete = Boolean(deleteModalTicket)
    && deleteConfirmation.trim() === deleteExpectedPhrase
    && deletingTicketId === null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Support" breadcrumbItem="Ticket System" />


          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
            <div className="d-flex gap-2 align-items-center">
              <Button
                color="light"
                size="sm"
                onClick={toggleFilters}
                className="d-flex align-items-center gap-1"
              >
                <i className="bx bx-filter-alt" style={{ fontSize: "16px" }} />
                Filters
                {hasActiveFilters && (
                  <Badge color="primary" pill className="ms-1 font-size-10">active</Badge>
                )}
                <i className={`bx bx-chevron-${filtersOpen ? "up" : "down"} ms-1`} style={{ fontSize: "14px" }} />
              </Button>
              <ButtonGroup>
                <Button
                  color={viewMode === "table" ? "primary" : "light"}
                  size="sm"
                  onClick={() => handleViewModeChange("table")}
                  title="Table view"
                  style={{ minWidth: 36 }}
                >
                  <i className="bx bx-list-ul" style={{ fontSize: "16px" }} />
                </Button>
                <Button
                  color={viewMode === "cards" ? "primary" : "light"}
                  size="sm"
                  onClick={() => handleViewModeChange("cards")}
                  title="Card view"
                  style={{ minWidth: 36 }}
                >
                  <i className="bx bx-grid-alt" style={{ fontSize: "16px" }} />
                </Button>
              </ButtonGroup>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Button
                color="secondary"
                outline
                onClick={() => void handleExportCsv()}
                disabled={exporting}
              >
                {exporting ? "Exporting..." : "Export CSV"}
              </Button>
              {canCreate && (
                <Button color="success" onClick={() => setShowCreateForm((state) => !state)}>
                  {showCreateForm ? "Hide create form" : "Create ticket"}
                </Button>
              )}
            </div>
          </div>

          {/* Collapsible filters */}
          <Collapse isOpen={filtersOpen}>
            <Row className="mb-3 g-2 align-items-end">
              <Col md={3}>
                <Label className="form-label">Search</Label>
                <Input
                  value={searchFilter}
                  onChange={(event) => setSearchFilter(event.target.value)}
                  placeholder="Search by subject or description"
                />
              </Col>
              <Col md={2}>
                <Label className="form-label">Status</Label>
                <Select
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  name="ticket_statuses"
                  value={getSelectedOptions(STATUS_OPTIONS, statusFilter)}
                  options={STATUS_OPTIONS}
                  styles={filterSelectStyles}
                  placeholder="Select statuses..."
                  onChange={(options: readonly FilterOption[] | null) => setStatusFilter(mapSelectValues(options))}
                />
              </Col>
              <Col md={2}>
                <Label className="form-label">Priority</Label>
                <Select
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  name="ticket_priorities"
                  value={getSelectedOptions(PRIORITY_FILTER_OPTIONS, priorityFilter)}
                  options={PRIORITY_FILTER_OPTIONS}
                  styles={filterSelectStyles}
                  placeholder="Select priorities..."
                  onChange={(options: readonly FilterOption[] | null) => setPriorityFilter(mapSelectValues(options))}
                />
              </Col>
              <Col md={2}>
                <Label className="form-label">Category</Label>
                <Select
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  name="ticket_categories"
                  value={getSelectedOptions(categoryFilterOptions, categoryFilter)}
                  options={categoryFilterOptions}
                  styles={filterSelectStyles}
                  placeholder="Select categories..."
                  onChange={(options: readonly FilterOption[] | null) => setCategoryFilter(mapSelectValues(options))}
                />
              </Col>
              <Col md={3} className="d-flex gap-2 justify-content-md-end">
                <Button
                  color="primary"
                  onClick={() => {
                    setPagination((prev) => ({ ...prev, current_page: 1 }));
                    void loadTickets(1);
                  }}
                  disabled={loading || !hasActiveFilters}
                >
                  Apply filters
                </Button>
                <Button
                  color="light"
                  onClick={() => {
                    setPagination((prev) => ({ ...prev, current_page: 1 }));
                    setStatusFilter([]);
                    setPriorityFilter([]);
                    setSearchFilter("");
                    setCategoryFilter([]);
                    void loadTickets(1, { status: [], priority: [], search: "", category_id: [] });
                  }}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Collapse>

          {showCreateForm && canCreate && (
            <Card className="mb-3">
              <CardBody>
                <Form onSubmit={handleCreate}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Label className="form-label">Subject *</Label>
                      <Input
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                        required
                        minLength={3}
                        maxLength={255}
                      />
                    </Col>
                    <Col md={2}>
                      <Label className="form-label">Priority</Label>
                      <Input
                        type="select"
                        value={priority}
                        onChange={(event) => setPriority(Number(event.target.value))}
                      >
                        <option value={1}>Critical</option>
                        <option value={2}>High</option>
                        <option value={3}>Medium</option>
                        <option value={4}>Low</option>
                      </Input>
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">Category</Label>
                      <Input
                        type="select"
                        value={createCategoryId}
                        onChange={(event) => setCreateCategoryId(event.target.value)}
                      >
                        <option value="">No category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={12}>
                      <Label className="form-label">Description *</Label>
                      <Input
                        type="textarea"
                        rows={5}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        required
                        minLength={3}
                      />
                    </Col>
                    <Col md={12} className="d-flex justify-content-end">
                      <Button type="submit" color="success" disabled={submitting}>
                        {submitting ? "Creating..." : "Create"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody>
              {loadError && tickets.length > 0 && (
                <Alert color="warning" fade={false} className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <span>{loadError}. Showing last loaded results.</span>
                  <Button color="warning" outline size="sm" onClick={() => void loadTickets(pagination.current_page || 1)}>
                    Retry
                  </Button>
                </Alert>
              )}
              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : loadError && tickets.length === 0 ? (
                <Alert color="danger" fade={false} className="mb-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <span>{loadError}</span>
                  <Button color="danger" outline size="sm" onClick={() => void loadTickets(pagination.current_page || 1)}>
                    Retry
                  </Button>
                </Alert>
              ) : tickets.length === 0 ? (
                <Alert color="light" fade={false} className="mb-0">
                  No tickets found.
                </Alert>
              ) : (
                <>
                {canBulk && selectedTicketIds.size > 0 && (
                  <div className="d-flex align-items-center gap-2 p-2 mb-2 bg-light rounded">
                    <strong>{selectedTicketIds.size} selected</strong>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={bulkAction}
                      onChange={(e) => { setBulkAction(e.target.value); setBulkValue(""); }}
                      style={{ width: 160 }}
                    >
                      <option value="">— Action —</option>
                      <option value="close">Close</option>
                      <option value="resolve">Resolve</option>
                      <option value="assign">Assign</option>
                      <option value="change_status">Change status</option>
                      <option value="change_priority">Change priority</option>
                      <option value="delete">Delete</option>
                    </Input>
                    {bulkAction === "assign" && (
                      <Input
                        type="select"
                        bsSize="sm"
                        value={bulkValue}
                        onChange={(e) => setBulkValue(e.target.value)}
                        style={{ width: 160 }}
                      >
                        <option value="">— User —</option>
                        {bulkAssignUsers.map((u) => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </Input>
                    )}
                    {bulkAction === "change_priority" && (
                      <Input
                        type="select"
                        bsSize="sm"
                        value={bulkValue}
                        onChange={(e) => setBulkValue(e.target.value)}
                        style={{ width: 120 }}
                      >
                        <option value="">— Priority —</option>
                        <option value="1">Critical</option>
                        <option value="2">High</option>
                        <option value="3">Medium</option>
                        <option value="4">Low</option>
                      </Input>
                    )}
                    {bulkAction === "change_status" && (
                      <Input
                        type="select"
                        bsSize="sm"
                        value={bulkValue}
                        onChange={(e) => setBulkValue(e.target.value)}
                        style={{ width: 150 }}
                      >
                        <option value="">— Status —</option>
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    )}
                    <Button
                      size="sm"
                      color={bulkAction === "delete" ? "danger" : "primary"}
                      disabled={!bulkAction || bulkProcessing || ((bulkAction === "assign" || bulkAction === "change_priority" || bulkAction === "change_status") && !bulkValue)}
                      onClick={() => void handleBulkAction()}
                    >
                      {bulkProcessing ? "Processing..." : "Apply"}
                    </Button>
                    <Button size="sm" color="light" onClick={() => { setSelectedTicketIds(new Set()); setBulkAction(""); }}>
                      Cancel
                    </Button>
                  </div>
                )}

                {viewMode === "table" ? (
                  <Table responsive hover className="align-middle mb-0">
                    <thead>
                      <tr>
                        {canBulk && (
                        <th style={{ width: 36 }}>
                          <Input
                            type="checkbox"
                            checked={selectedTicketIds.size === ownTickets.length && ownTickets.length > 0}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        )}
                        <th>ID</th>
                        <th>Subject</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Category</th>
                        <th>Creator</th>
                        <th>Assignee</th>
                        <th>SLA</th>
                        <th>Last activity</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => (
                        <TicketRow
                          key={ticket.id}
                          ticket={ticket}
                          deletingTicketId={deletingTicketId}
                          onAccept={handleAccept}
                          onDelete={openDeleteModal}
                          selected={selectedTicketIds.has(ticket.id)}
                          onToggleSelect={toggleSelectTicket}
                          showCheckbox={canBulk}
                          disableCheckbox={!isOwnTicket(ticket)}
                        />
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <>
                    {canBulk && (
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <Input
                        type="checkbox"
                        checked={selectedTicketIds.size === ownTickets.length && ownTickets.length > 0}
                        onChange={toggleSelectAll}
                      />
                      <small className="text-muted">Select all</small>
                    </div>
                    )}
                    <Row>
                      {tickets.map((ticket) => (
                        <TicketCard
                          key={ticket.id}
                          ticket={ticket}
                          deletingTicketId={deletingTicketId}
                          onAccept={handleAccept}
                          onDelete={openDeleteModal}
                          selected={selectedTicketIds.has(ticket.id)}
                          onToggleSelect={toggleSelectTicket}
                          showCheckbox={canBulk}
                          disableCheckbox={!isOwnTicket(ticket)}
                        />
                      ))}
                    </Row>
                  </>
                )}
                </>
              )}

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Total: {pagination.total} | Page {pagination.current_page} / {pagination.total_page}
                </small>
                <div className="d-flex gap-2">
                  <Button
                    color="light"
                    size="sm"
                    disabled={!pagination.prev_page || loading}
                    onClick={() => loadTickets(Math.max(1, pagination.current_page - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    disabled={!pagination.next_page || loading}
                    onClick={() => loadTickets(pagination.current_page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={Boolean(deleteModalTicket)} toggle={closeDeleteModal} centered>
        <ModalHeader toggle={closeDeleteModal}>
          {deleteModalTicket ? `Delete Ticket #${deleteModalTicket.id}` : "Delete Ticket"}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">This action is irreversible.</p>
          <p className="mb-3">
            Type <code>{deleteExpectedPhrase}</code> to confirm deletion.
          </p>
          <Input
            value={deleteConfirmation}
            onChange={(event) => setDeleteConfirmation(event.target.value)}
            placeholder={deleteExpectedPhrase}
            disabled={deletingTicketId !== null}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeDeleteModal} disabled={deletingTicketId !== null}>
            Cancel
          </Button>
          <Button color="danger" onClick={() => void handleDelete()} disabled={!canConfirmDelete}>
            {deletingTicketId !== null ? "Deleting..." : "Delete ticket"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default TicketsPage;
